/* eslint-disable no-restricted-globals */
import { formatDistanceToNow, parseISO } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import i18next, { t } from 'i18next';
import { marked } from 'marked';
import React from 'react';
import * as XLSX from 'xlsx';
import { ReactComponent as FullTimeIcon } from '../assets/icons/FullTimeIcon.svg';
import { ReactComponent as IntershipIcon } from '../assets/icons/IntershipIcon.svg';
import { ReactComponent as PartTimeIcon } from '../assets/icons/PartTimeIcon.svg';
import { oneDecimalSubMetric, trainingCostMetrics } from './constant';

function formatNumberWithLetters(numberString = 0) {
  const number = parseInt(numberString, 10);

  if (Number.isNaN(number)) {
    throw new Error('Input is not a valid number');
  }

  let formattedNumber;

  if (number >= 1000000000) {
    formattedNumber = `${(number / 1000000000).toFixed(2).replace(/\.0+$/, '')}B`;
  } else if (number >= 1000000) {
    formattedNumber = `${(number / 1000000).toFixed(2).replace(/\.0+$/, '')}M`;
  } else if (number >= 1000) {
    formattedNumber = `${(number / 1000).toFixed(2).replace(/\.0+$/, '')}K`;
  } else {
    formattedNumber = number.toString();
  }

  return formattedNumber.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

function addSpacesToNumberString(input) {
  const numberString = String(input);
  return numberString.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

function convertInYears(data) {
  const value = parseFloat(data);

  if (Number.isNaN(value)) {
    return <span>Data not available.</span>;
  }

  const years = Math.floor(value);
  const months = Math.round((value - years) * 12);

  return (
    <span>
      {years}
      <span className='text-base font-Satoshi'> years</span>{' '}
      {months !== 0 && (
        <>
          {months}
          <span className='text-base font-Satoshi'> months</span>
        </>
      )}
    </span>
  );
}

function getYearMonthObject(input) {
  const isNegative = parseFloat(input) < 0;
  const value = Math.abs(parseFloat(Number(input).toFixed(2)));
  let years = Math.floor(value);
  let months = Math.round((value - years) * 12);

  if (months === 12) {
    months = 0;
    years += 1;
  }

  return {
    years: isNegative ? -years : years,
    months,
    isNegative,
  };
}

function convertAgeToYearsAndMonths({ age, yearsText, monthsText, className }) {
  const { years, months, isNegative } = getYearMonthObject(age);

  const yearDisplay = isNegative ? `-${Math.abs(years)}` : `${years}`;

  if (yearsText) {
    return (
      <span>
        {yearDisplay}
        <span className={className}> {yearsText}</span> {months}
        <span className={className}> {monthsText}</span>
      </span>
    );
  }

  return {
    years,
    months,
  };
}

function formatDurationInYearsAndMonths({ duration, yearsText, monthsText, className }) {
  let years = 0;
  let months = 0;

  if (typeof duration === 'string') {
    const trimmed = duration.trim();
    const regex = /^(?:(\d+)\s*(?:year|years))?\s*(?:(\d+)\s*(?:month|months))?$/i;
    const match = trimmed.match(regex);
    if (match) {
      years = match[1] ? parseInt(match[1], 10) : 0;
      months = match[2] ? parseInt(match[2], 10) : 0;
    }
  } else {
    years = Math.floor(duration);
    months = Math.round((duration - years) * 12);
  }

  if (months === 12) {
    months = 0;
    years += 1;
  }

  let finalYearsText = yearsText;
  if (i18next.language === 'fr') {
    finalYearsText = years === 1 ? t('global.year') : t('global.years');
  } else {
    finalYearsText = years === 1 ? t('global.year') : t('global.years');
  }

  let finalMonthsText = monthsText;
  if (i18next.language !== 'fr') {
    finalMonthsText = months === 1 ? t('global.month') : t('global.months');
  }

  if (years === 0 && months !== 0) {
    return (
      <span>
        {months}
        <span className={className}> {finalMonthsText}</span>
      </span>
    );
  }

  if (months === 0 && years !== 0) {
    return (
      <span>
        {years}
        <span className={className}> {finalYearsText}</span>
      </span>
    );
  }

  return (
    <span>
      {years}
      <span className={className}> {finalYearsText}</span> {months}
      <span className={className}> {finalMonthsText}</span>
    </span>
  );
}

function formatNumberWithSpace(number) {
  const numberString = number.toString();

  const [integerPart, decimalPart] = numberString.split('.');

  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
}

function capitalizeFirstLetter(string) {
  if (string?.length === 0 || string === null || string === undefined) return '';
  String(string);
  return string
    .toLowerCase()
    .split(' ')
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}
const getDate = (date) => {
  const year = date.toLocaleString('default', { year: 'numeric' });
  const month = date.toLocaleString('default', { month: '2-digit' });
  const day = date.toLocaleString('default', { day: '2-digit' });
  return `${year}-${month}-${day}`;
};
const formatDate = (date) => {
  const adjustedDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return adjustedDate.toISOString().split('T')[0];
};

const getRelativeTimeString = (updatedAt) => {
  const date = parseISO(updatedAt);
  const timeAgo = formatDistanceToNow(date, {
    addSuffix: true,
    locale: i18next.language === 'fr' ? fr : enUS,
  });

  return timeAgo;
};

const formatValueToAbbreviatedEuro = (value, decimals = 2, enablePercentValue = false) => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return `0 ${enablePercentValue ? '' : '€'}`;
  }

  const thresholds = [
    { threshold: 1e12, suffix: ` T${enablePercentValue ? '' : '€'}` },
    { threshold: 1e9, suffix: ` B${enablePercentValue ? '' : '€'}` },
    { threshold: 1e6, suffix: ` M${enablePercentValue ? '' : '€'}` },
    { threshold: 1e3, suffix: ` k${enablePercentValue ? '' : '€'}` },
  ];

  const absValue = Math.abs(Number(value));

  // eslint-disable-next-line no-restricted-syntax
  for (const { threshold, suffix } of thresholds) {
    if (absValue >= threshold) {
      return `${(value / threshold).toFixed(decimals)}${suffix}`;
    }
  }
  return `${Number(value).toFixed(decimals)} ${enablePercentValue ? '' : '€'}`;
};

const convertMillisecondToDateAndMonths = (
  milliseconds,
  returnDecimalFormat = true,
  isGraphLabel = true
) => {
  let formattedMillisecond = milliseconds;
  const msInSecond = 1000;
  const msInMinute = msInSecond * 60;
  const msInHour = msInMinute * 60;
  const msInDay = msInHour * 24;
  const msInMonth = msInDay * 30;

  const months = Math.floor(formattedMillisecond / msInMonth);
  formattedMillisecond %= msInMonth;
  const days = Math.floor(formattedMillisecond / msInDay);
  if (!isGraphLabel) {
    return (
      <span>
        {months}
        <span className='text-[15px] font-Satoshi font-medium'> months</span> {days}
        <span className='text-[15px] font-Satoshi font-medium'> days</span>
      </span>
    );
  }
  if (returnDecimalFormat) {
    const totalMonths = `${months}.${days}`;
    return Number(totalMonths);
  }

  if (months === 0 && days <= 31) {
    return `${days} days`;
  }

  if (months > 0) {
    return `${months} month${months > 1 ? 's' : ''} ${days > 0 ? `${days} days` : ''}`.trim();
  }

  return `${days} days`;
};

function formatYearMonth(data, params) {
  const {
    className = '',
    yearsText = 'years',
    monthsText = 'months',
    isGraphLabel = false,
  } = params || {};

  const { years, months, isNegative } = getYearMonthObject(data);
  const yearDisplay = isNegative ? `-${Math.abs(years)}` : `${years}`;

  if (!isGraphLabel) {
    return (
      <span>
        {yearDisplay}
        <span className={className}> {yearsText}</span> {months}
        <span className={className}> {monthsText}</span>
      </span>
    );
  }

  return `${yearDisplay.toString().padStart(2, '0')} ${t('global.years')} ${months.toString().padStart(2, '0')} ${t('global.month')}`;
}

export const isSalaryMetric = (metric) => {
  const metrics = [
    'GrossSalary',
    'AverageSalary',
    'NetSalary',
    'AverageNetSalary',
    'AverageTrainingCostPerEmployee',
    'AverageTrainingSalaryCostPerEmployee',
    'AverageHourlyWage',
    'AverageTotalTrainingCostPerEmployee',
    'AverageTotalTrainingCostPerTrainee',
    'AverageTrainingCostPerTrainee',
    'AverageTrainingSalaryCostPerTrainee',
    'MedianSalary',
    'MedianNetSalary',
    'MinSalary',
    'MinNetSalary',
    'MaxSalary',
    'MaxNetSalary',
    'Q1Salary',
    'Q1NetSalary',
    'Q3Salary',
    'Q3NetSalary',
    'AnnualChangeSalary',
    'AverageCumulativeSalary',
    'TotalChargedSalary',
    'GrossSalaryPerFTE',
    'ChargedSalaryPerFTE',
    'NetSalaryPerFTE',
  ];
  return metrics.includes(metric);
};

export const isPercentage = (subMetric) => {
  const percentageMetrics = new Set([
    'Turnover Rate',
    'Attrition Rate',
    'AttritionRateVoluntary',
    'TurnoverRateVoluntary',
    'FailedHiresRatio3Months',
    'FailedHiresRatio6Months',
    'FailedHiresRatio12Months',
    'AbsenteeismRate',
    'GapGenderRateGrossSalary',
    'CancelSessionsRate',
    'ManagerRatio',
    'RateOfTrainees',
    'AbsenteeismRateTraining',
    'Absenteeism Rate',
    'AnnualChangeRateSalary',
    'FemaleRate',
    'RetentionRate',
  ]);
  return percentageMetrics.has(subMetric);
};

function thousandsFormatWithSpaces(value) {
  return new Intl.NumberFormat('en-GB', { useGrouping: true }).format(value).replace(/,/g, ' ');
}

const hasDecimal = (num) => num % 1 !== 0;
const formatNumber = (num, subMetric) => {
  const thousandsFormatWithoutDecimal = [
    'Headcount Begin Period',
    'Headcount End Period',
    'Arrivals',
    'OpenJobs',
    'Candidatures',
    'NumberOfHire',
    'Departures',
    'NumberOfTrainees',
    'NumberOfEmployeesTrained',
    'Total',
    'Headcount Average',
  ];
  const decimalFormat = oneDecimalSubMetric.has(subMetric) ? 1 : 2;

  if (subMetric) {
    if (thousandsFormatWithoutDecimal.includes(subMetric)) {
      return thousandsFormatWithSpaces(Number(num)?.toFixed(0));
    }
  }
  if (typeof num === 'number' && !isNaN(num)) {
    if (oneDecimalSubMetric.has(subMetric)) {
      return Number(num).toFixed(1);
    }
    return hasDecimal(num) ? num.toFixed(decimalFormat) : Math.trunc(num);
  }
  return num;
};

function formatSignedNumber(value, subMetric, currencySymbol = '€') {
  if (isNaN(value)) return '';

  const formattedValue = value >= 0 ? `+${value}` : `${value}`;
  if (subMetric !== 'GapGenderAbsoluteGrossSalary') {
    return formattedValue;
  }
  return `${formattedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ${currencySymbol}`;
}

function formatAbsenceDays(value, params, subMetric) {
  const { isGraphLabel, isSubKpi } = params || {};

  if (isNaN(value)) return '';

  if (isSubKpi) return formatNumber(value);
  if (!isGraphLabel) {
    return (
      <span>
        {subMetric === 'AverageAbsenteeismDay'
          ? thousandsFormatWithSpaces(value?.toFixed(2))
          : thousandsFormatWithSpaces(value)}
        <span className='text-xl '> {t('Dashboards.DaysOfAbsence')} </span>
      </span>
    );
  }

  return `${value.toFixed(1)} ${t('Dashboards.DaysOfAbsence')}`;
}

function formatValueToCurrency(value, symbol = '€') {
  if (isNaN(value)) return '';

  return `${Number(value)
    .toFixed(2)
    ?.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ${symbol}`;
}

const formatValueToThousandsEuro = (value, decimals = 0, enablePercentValue = false) => {
  if (enablePercentValue) {
    return formatValueToAbbreviatedEuro(value, decimals, enablePercentValue);
  }
  if (isNaN(value) || value === null || value === undefined) return '';

  const thousands = value / 1000;
  const formattedThousands = thousands.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  return `${formattedThousands} K€`;
};

const convertMetricValue = ({
  subMetric,
  value,
  decimals = 0,
  chartType,
  enablePercentValue,
  params,
}) => {
  const graphsToIgnore = new Set(['donut', 'stack bar', 'bar', 'line', 'bubble']);
  const chartTypesWithAbbreviatedEuro = ['donut', 'stack bar', 'bar'];
  switch (true) {
    case subMetric === 'Age average' || subMetric === 'AverageTenure':
      return graphsToIgnore.has(chartType)
        ? formatNumber(value)
        : formatYearMonth(value, { isGraphLabel: true, ...params });

    case subMetric === 'GrossSalary':
      return `${formatValueToThousandsEuro(value, decimals, enablePercentValue)}`;

    case subMetric === 'TotalChargedSalary':
      return `${formatValueToThousandsEuro(value, decimals, enablePercentValue)}`;

    case subMetric === 'NetSalary':
      return `${formatValueToThousandsEuro(value, decimals, enablePercentValue)}`;

    case isSalaryMetric(subMetric):
      return chartType === 'metric'
        ? `${thousandsFormatWithSpaces(value)} ${enablePercentValue ? '' : '€'}`
        : formatValueToAbbreviatedEuro(value, decimals, enablePercentValue);

    case isPercentage(subMetric):
      return `${Number(value).toFixed(2)}%`;

    case subMetric === 'TimeToHire':
      return convertMillisecondToDateAndMonths(value, graphsToIgnore.has(chartType));

    case subMetric === 'GapGenderRateGrossSalary' || subMetric === 'GapGenderAbsoluteGrossSalary':
      return formatSignedNumber(value, subMetric);

    case (subMetric === 'TotalAbsenceDays' || subMetric === 'AverageAbsenteeismDay') &&
      chartType === 'metric':
      return formatAbsenceDays(value, params, subMetric);

    case trainingCostMetrics.includes(subMetric):
      return chartTypesWithAbbreviatedEuro.includes(chartType)
        ? formatValueToAbbreviatedEuro(value, decimals, enablePercentValue)
        : formatValueToCurrency(value, enablePercentValue ? '' : '€');

    case subMetric === 'FTE':
    case subMetric === 'NumberOfTrainingHrs':
      return thousandsFormatWithSpaces(Number(value).toFixed(2));

    default:
      return formatNumber(value, subMetric);
  }
};
function capitalizeFirstLetterOfSentences(string) {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}

function cleanStreamChunk(chunk) {
  return chunk
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .join('\n');
}

const highlightAndCapitalizeTextWithSearch = (text, searchInput) => {
  const capitalizedText = text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : '';

  if (!searchInput) {
    return capitalizedText;
  }

  const escapedSearchInput = searchInput.replace(/([.*+?=^!:${}()|[\]/\\])/g, '\\$1');

  const parts = capitalizedText.split(new RegExp(`(${escapedSearchInput})`, 'gi'));

  return parts.map((part, index) => {
    const isMatch = part?.toLowerCase() === searchInput.toLowerCase();
    if (isMatch) {
      return (
        // eslint-disable-next-line react/no-array-index-key
        <span key={index} style={{ backgroundColor: '#5462FF4D' }}>
          {part}
        </span>
      );
    }
    return part;
  });
};

const formatTitleCase = (value) => {
  return value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (char) => char.toUpperCase());
};

function formatChartDisplayValue({
  value,
  subMetric,
  chartType = 'bar',
  // percentage = false, // Unused parameter
  enablePercentValue = false,
}) {
  const formattedValue = convertMetricValue({ value, subMetric, chartType, enablePercentValue });
  return formattedValue;
}

function translateLabel(label) {
  if (typeof label !== 'string') return label;

  const replacements = {
    months: t('global.months'),
    month: t('global.month'),
    days: t('global.days'),
    day: t('global.day'),
    years: t('global.years'),
    year: t('global.year'),
    Yes: t('Dashboards.Yes'),
    No: t('global.No'),
    Not: t('Dashboards.Not'),
    Female: t('Dashboards.Female'),
    Male: t('Dashboards.Male'),
    French: t('global.French'),
    Foreigner: t('global.Foreigner'),
  };

  return Object.entries(replacements).reduce((result, [key, value]) => {
    const regex = new RegExp(`\\b${key}\\b`, 'gi');
    return String(result).replace(regex, value);
  }, label);
}

function getPercentageChange(value) {
  if (Number(value) === 0) {
    return `No data`;
  }
  return `${value}%`;
}

function excelFileFormatter({ rowsForExcel, headers, saveToFile, fileExtension }) {
  try {
    const worksheet = XLSX.utils.json_to_sheet(rowsForExcel, { header: headers });
    const colWidths = headers.map((header) => {
      const maxLength = Math.max(
        header.length,
        ...rowsForExcel.map((row) => (row[header] ? String(row[header]).length : 0))
      );
      return { wch: maxLength + 4 };
    });

    worksheet['!cols'] = colWidths;

    worksheet['!freeze'] = { xSplit: 0, ySplit: 1 };

    headers.forEach((header, idx) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: idx });
      if (!worksheet[cellRef]) return;

      worksheet[cellRef].s = {
        font: { bold: true },
        alignment: { vertical: 'center', horizontal: 'center' },
      };
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Overview');

    if (saveToFile) {
      XLSX.writeFile(workbook, `overview-data.${fileExtension}`);
      return null;
    }

    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'binary', cellStyles: true });

    const binaryStringToBase64 = (s) => {
      return window.btoa(
        s
          .split('')
          // eslint-disable-next-line no-bitwise
          .map((c) => String.fromCharCode(c.charCodeAt(0) & 0xff))
          .join('')
      );
    };

    return {
      excelBase64: binaryStringToBase64(wbout),
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error creating Excel file:', error);
    return null;
  }
}

function downloadCSV({ content, fileExtension = 'csv', fileName = 'file', shouldDownload = true }) {
  if (!shouldDownload || !content) return;

  const blob = new Blob([content], {
    type: `text/${fileExtension};charset=utf-8`,
  });

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');

  a.setAttribute('hidden', '');
  a.setAttribute('href', url);
  a.setAttribute('download', `${fileName}.${fileExtension}`);

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
function checkIfValidDataForLegend(data) {
  return (
    Array.isArray(data) &&
    data.length > 0 &&
    data.some((item) => item && typeof item === 'object' && 'name' in item)
  );
}

function applyTranslationsToChart(chartOptions) {
  const updatedOptions = { ...chartOptions };

  if (updatedOptions.labels) {
    updatedOptions.labels = updatedOptions.labels.map((label) => translateLabel(label));

    if (Array.isArray(updatedOptions.data)) {
      updatedOptions.data = updatedOptions.data.map((item) => ({
        ...item,
        name: translateLabel(item.name),
      }));
    }
  } else {
    updatedOptions.series = updatedOptions.series.map((s) => ({
      ...s,
      name: translateLabel(s.name),
    }));
    updatedOptions.xaxis.categories = updatedOptions?.xaxis?.categories.map((category) =>
      translateLabel(category)
    );
  }

  return updatedOptions;
}

const truncateText = (text, maxLength = 25) => {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};
const formatDateForDataExtractor = (date) => {
  if (!date) return '-';

  return new Date(date).toLocaleDateString('fr-FR');
};

const capitalizeText = (text, defaultValue = '-') => {
  return text ? capitalizeFirstLetter(text) : defaultValue;
};

const standardCell = (content, extraClasses = '') => (
  <td className={`py-4 px-2 ${extraClasses}`}>{content}</td>
);

const badgeCell = (Icon, text) => (
  <td className='py-4 px-2'>
    <div className='bg-[#E4E9F2] rounded-full border flex items-center justify-start gap-2 p-2 text-nowrap'>
      <Icon />
      {capitalizeText(text)}
    </div>
  </td>
);

const sortByTenureMacro = (array = []) => {
  return array.slice().sort((a, b) => {
    const getNum = (str) => parseInt(str?.match(/\d+/)?.[0] || 0, 10);
    return getNum(a) - getNum(b);
  });
};

const injectIcons = (data, iconMap, toolTipIconMap = null) => {
  return data.map((item) => ({
    ...item,
    Icon: iconMap[item.Icon] || null,
    ...(toolTipIconMap &&
      item.toolTipIcon && {
        toolTipIcon: toolTipIconMap[item.toolTipIcon] || null,
      }),
    ...(item.toolTipTitle && {
      toolTipTitle: t(item.toolTipTitle),
    }),
  }));
};
function showContractType(employmentType) {
  const contractTypeStyles = {
    'Part-time': 'bg-[#FFF8E1] text-[#FFA000]',
    'Full-time': 'bg-[#F0FFF5] text-[#00B887]',
    Internship: 'bg-[#FFF2F2] text-[#FF3D71]',
    CDD: 'bg-[#FFF8E1] text-[#FFA000]',
    CDI: 'bg-[#F0FFF5] text-[#00B887]',
    'ALTERNANCE-STAGE': 'bg-[#FFF2F2] text-[#FF3D71]',
  };
  const contractTypeIcons = {
    'Part-time': <PartTimeIcon />,
    'Full-time': <FullTimeIcon />,
    Internship: <IntershipIcon />,
    CDD: <PartTimeIcon />,
    CDI: <FullTimeIcon />,
    'ALTERNANCE-STAGE': <IntershipIcon />,
  };
  return (
    <span
      className={`flex items-center gap-2 rounded-full justify-start w-fit px-4 py-1 text-sm ${contractTypeStyles[employmentType]} text-nowrap`}
    >
      {contractTypeIcons[employmentType]}{' '}
      {employmentType === 'CDI' || employmentType === 'CDD' ? (
        employmentType
      ) : (
        <span className='capitalize'>{employmentType?.toLowerCase()}</span>
      )}
    </span>
  );
}

const formatTextWithMentions = (text, mentionedUsers = [], employeesData = []) => {
  if (!text || !mentionedUsers || mentionedUsers.length === 0) return text;

  let formattedText = text;
  mentionedUsers.forEach((userId) => {
    const user = employeesData.find((u) => u.id === userId);
    if (user) {
      const userName = `${user.last_name} ${user.first_name}`;
      const regex = new RegExp(`@${userName}\b`, 'g');
      formattedText = formattedText.replace(regex, `<strong>@${userName}</strong>`);
    }
  });
  return formattedText;
};

function stringToColor(string) {
  let hash = 0;
  for (let i = 0; i < string.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (let i = 0; i < 3; i += 1) {
    // eslint-disable-next-line no-bitwise
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
}

function stringAvatar(name, size = 32, fontSize = 14) {
  const nameParts = (name || '').split(' ');
  return {
    sx: {
      bgcolor: stringToColor(name || '') || '#3546FF14',
      width: size,
      height: size,
      fontSize,
      fontWeight: 600,
      fontFamily: 'ClashDisplay-Semibold',
      textTransform: 'uppercase',
      color: '#fff',
      letterSpacing: '0.1em',
      textAlign: 'center',
    },
    children: ((nameParts[0]?.[0] || '') + (nameParts[1]?.[0] || '')).toUpperCase(),
    'aria-label': name,
  };
}

function convertMarkdownToText(markdown) {
  const toHtml = marked(markdown);
  return toHtml.replace(/<[^>]+>/g, '');
}

function preprocessInsight(text) {
  if (!text || typeof text !== 'string') {
    return '';
  }

  // Remove any remaining SSE format artifacts (though fetchEventSource should handle this)
  let processed = text.replace(/^data:\s*/gm, '');

  // Remove null bytes and other control characters that could break markdown
  // eslint-disable-next-line no-control-regex
  processed = processed.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '');

  // Fix headers that appear in the middle of text (after punctuation or text)
  // Pattern: non-# character followed by header (not already a header)
  // Example: "internes.# Plan d'action" -> "internes.\n\n# Plan d'action"
  // Exclude cases where # already appears (to avoid splitting ## into # #)
  processed = processed.replace(/([^\n#])(#{1,6}\s+)/g, '$1\n\n$2');

  // Fix headers that are concatenated without proper spacing
  // Pattern: header text followed immediately by another header
  // Example: "# Analyse## Cause" -> "# Analyse\n\n## Cause"
  processed = processed.replace(/(#{1,6}\s+[^\n#]+?)(#{1,6}\s+)/g, '$1\n\n$2');

  // Fix headers followed immediately by text that starts a new sentence/paragraph
  // Pattern: header text ending (no trailing space), then capital letter without space/newline
  // Example: "## Cause racineL'absence" -> "## Cause racine\n\nL'absence"
  // This matches when header text ends and immediately a capital letter follows (new paragraph)
  processed = processed.replace(
    /(#{1,6}\s+[^\n#\s]+(?:\s+[^\n#\s]+)*)([A-ZÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖØÙÚÛÜÝÞŸ])/g,
    '$1\n\n$2'
  );

  // Ensure proper spacing after headers at start of line
  // Fix headers without space after # markers
  processed = processed.replace(/^(#{1,6})([^\s#\n])/gm, '$1 $2');

  // Remove standalone header markers on their own line (incomplete headers)
  processed = processed.replace(/^(#{1,6})\s*$/gm, '');

  // Fix list items that are concatenated together (after punctuation or text)
  // Pattern: text ending with punctuation or letter, then list item marker with space
  // Example: "effectif.- La catégorie" -> "effectif.\n\n- La catégorie"
  // Avoid matching ** (bold markdown) by ensuring space after marker
  // Match: character (not * to avoid **), then - or * followed by space
  processed = processed.replace(/([^\n*])([-]\s+)/g, '$1\n\n$2');
  // For * list items, ensure it's not part of ** formatting
  // Match: non-* character, then * followed by space and letter (actual list item)
  processed = processed.replace(
    /([^\n*])([*]\s+[A-ZÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖØÙÚÛÜÝÞŸa-zàáâãäåæçèéêëìíîïðñòóôõöøùúûüýþÿ])/g,
    '$1\n\n* $2'
  );

  // Fix headers followed immediately by list items
  // Pattern: header text ending, then list item
  // Example: "### Actions recommandées- Surveiller" -> "### Actions recommandées\n\n- Surveiller"
  processed = processed.replace(/(#{1,6}\s+[^\n#]+?)([-]\s+)/g, '$1\n\n$2');
  processed = processed.replace(
    /(#{1,6}\s+[^\n#]+?)([*]\s+[A-ZÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖØÙÚÛÜÝÞŸa-zàáâãäåæçèéêëìíîïðñòóôõöøùúûüýþÿ])/g,
    '$1\n\n* $2'
  );

  // Fix incomplete list items - only if at start of line with proper indentation
  // Match: start of line, optional spaces, - or *, then non-space character
  processed = processed.replace(/^(\s*)([-*])([^\s-*])/gm, '$1$2 $3');

  // Normalize line breaks - replace multiple consecutive newlines with at most two
  // (preserves paragraph breaks in markdown)
  processed = processed.replace(/\n{3,}/g, '\n\n');

  // Remove trailing whitespace from each line
  processed = processed.replace(/[ \t]+$/gm, '');

  // Trim leading/trailing whitespace
  processed = processed.trim();

  // Ensure the result is not empty after all processing
  if (!processed) {
    return '';
  }

  return processed;
}

const formatAsText = (value) => {
  if (value == null) return '—';
  if (Array.isArray(value)) return value.map(formatAsText).join(', ');
  if (value instanceof Date) return value.toLocaleDateString();
  if (typeof value === 'object') {
    return (
      value.name ?? value.title ?? value.label ?? value.email ?? value.id ?? JSON.stringify(value)
    );
  }
  return String(value);
};

const formatDateAsText = (input) => {
  if (!input) return '—';

  if (typeof input === 'object' && typeof input.toDate === 'function') {
    const d = input.toDate();
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
  }

  if (typeof input === 'object' && typeof input.valueOf === 'function') {
    const v = input.valueOf();
    const d = new Date(typeof v === 'number' ? v : String(v));
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
  }

  if (input instanceof Date) {
    return isNaN(input.getTime()) ? '—' : input.toLocaleDateString();
  }

  const d = typeof input === 'number' ? new Date(input) : new Date(String(input));
  return isNaN(d.getTime()) ? String(input) : d.toLocaleDateString();
};

const getTrimmedString = (string) => {
  return string?.trim()?.split(' ')?.join('_');
};

const getThreeYearPeriodFromDates = (dates) => {
  if (!Array.isArray(dates) || dates.length === 0) {
    return { dates: [] };
  }

  const lastDateStr = dates[dates.length - 1].trim();
  const lastDate = new Date(lastDateStr);

  if (isNaN(lastDate.getTime())) {
    return { dates: [] };
  }

  const currentYear = lastDate.getFullYear();

  const startYear = currentYear - 2;
  const month = String(lastDate.getMonth() + 1).padStart(2, '0');
  const day = String(lastDate.getDate()).padStart(2, '0');

  const startDate = `${startYear}-${month}-${day}`;
  const endDate = `${currentYear}-${month}-${day}`;

  return { dates: [startDate, endDate] };
};

const getYearFromPeriod = (period) => {
  if (!period) {
    return null;
  }
  if (typeof period === 'string') {
    return period;
  }
  if (!Array.isArray(period) || period.length < 2) {
    return null;
  }

  const [startDate, endDate] = period;

  const startYear = new Date(startDate).getFullYear();
  const endYear = new Date(endDate).getFullYear();

  return startYear === endYear ? startYear : endYear;
};

const formatISODateToDDMMYYYY = (dateString) => {
  if (!dateString || typeof dateString !== 'string' || !dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return '';
  }
  const [year, month, day] = dateString.split('-');
  return `${day}-${month}-${year}`;
};
export {
  addSpacesToNumberString,
  applyTranslationsToChart,
  badgeCell,
  capitalizeFirstLetter,
  capitalizeFirstLetterOfSentences,
  capitalizeText,
  checkIfValidDataForLegend,
  cleanStreamChunk,
  convertAgeToYearsAndMonths,
  convertInYears,
  convertMarkdownToText,
  convertMetricValue,
  convertMillisecondToDateAndMonths,
  downloadCSV,
  excelFileFormatter,
  formatAbsenceDays,
  formatAsText,
  formatChartDisplayValue,
  formatDate,
  formatDateAsText,
  formatDateForDataExtractor,
  formatDurationInYearsAndMonths,
  formatNumberWithLetters,
  formatNumberWithSpace,
  formatSignedNumber,
  formatTextWithMentions,
  formatTitleCase,
  formatValueToAbbreviatedEuro,
  formatValueToCurrency,
  formatValueToThousandsEuro,
  formatYearMonth,
  getDate,
  getPercentageChange,
  getRelativeTimeString,
  getThreeYearPeriodFromDates,
  getTrimmedString,
  getYearFromPeriod,
  highlightAndCapitalizeTextWithSearch,
  injectIcons,
  preprocessInsight,
  showContractType,
  sortByTenureMacro,
  standardCell,
  stringAvatar,
  stringToColor,
  thousandsFormatWithSpaces,
  translateLabel,
  truncateText,
  formatISODateToDDMMYYYY,
};
